import React, { useCallback } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ErrorStateCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Error message to display */
  message?: string;
  
  /** Callback function for retry button */
  onRetry?: () => void;
  
  /** Whether to show as a full card or compact alert */
  variant?: 'card' | 'alert';
  
  /** Severity level for alert variant */
  severity?: 'error' | 'warning';
}

/**
 * ErrorStateCard Component
 * 
 * Displays an error state with appropriate messaging and retry functionality.
 * Supports both card and alert variants for different use cases.
 * 
 * @param props - ErrorStateCardProps
 * @returns React component displaying the error state
 */
export const ErrorStateCard: React.FC<ErrorStateCardProps> = ({
  message = 'Unable to load data',
  onRetry,
  variant = 'card',
  severity = 'error',
  ...restProps
}) => {
  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    }
  }, [onRetry]);

  // Alert variant - compact error message
  if (variant === 'alert') {
    return (
      <Alert
        severity={severity}
        onClose={onRetry ? handleRetry : undefined}
        role="alert"
        sx={{ mb: 2 }}
      >
        {message}
        {onRetry && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleRetry}
              sx={{ mt: 1 }}
            >
              Retry
            </Button>
          </Box>
        )}
      </Alert>
    );
  }

  // Card variant - full error display with icon
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        borderLeft: '4px solid #F44336', // Error red
      }}
      role="alert"
      {...restProps}
    >
      <ErrorOutlineIcon
        sx={{
          fontSize: 48,
          color: '#F44336',
          mb: 2,
        }}
      />
      
      <Typography
        variant="body1"
        align="center"
        gutterBottom
        sx={{ mb: 2, maxWidth: 300 }}
      >
        {message}
      </Typography>

      {onRetry && (
        <Button
          variant="outlined"
          color="error"
          onClick={handleRetry}
          size="medium"
        >
          Try Again
        </Button>
      )}
    </Paper>
  );
};

export default ErrorStateCard;
